// The itinerary editor — the most active and fragile screen. Read
// docs/state-model.md before changing.
//
// Two modes:
//  - AUTO:   localPlan === null; layout computed by packItinerary(selectedIds,
//            dayCount, dateStart, allIdeas) on every render.
//  - MANUAL: localPlan is string[][] (idea ids per day); rendering uses
//            buildDaysFromPlan. Any explicit place/move from auto mode
//            freezes the visible layout into localPlan and calls
//            onSavePlan(plan) → App serializes it as a JSON string into
//            trip.customPlan (Firestore can't store nested arrays).
//
// Parking lot = selected but not in any plan row (manual mode only).
// Three layouts (list / sidebar / calendar) + map; per-idea overrides
// (time/duration/rename) live in trip.ideaOverrides.
// Exports packItinerary, todBucket, computeEventLanes for reuse/tests.
import React from 'react';
import { PALETTE, headerPad } from '../theme';
import { Icon, IconBtn } from '../components/UI';
import { CAT_COLORS } from '../data';
import { BookingScreen } from './BookingScreen';
import { bookingInfoForIdea, legDateRanges } from '../utils/bookingLinks';
import { IdeaBar } from '../components/IdeaBar';
import { MapView } from '../components/MapView';
import {
  fillEmptySlots, packItinerary, packMultiLegItinerary, buildDaysFromPlan, fmtTime, getTripLabel,
  parseFlightHM, parseFlightStr, resizePlanRows,
} from './itinerary/logic';

import { EditItemSheet, BookMiniSheet, BookedDetailSheet, LegDetailSheet, AddSheet, ScanSheet, TimePicker, DUR_PRESETS } from './itinerary/sheets';
import { IdeasPanel, PresetsPanel, DatesPanel } from './itinerary/panels';
import { CalendarView, loadCalSettings, CAL_SETTINGS_KEY, CalendarSettingsPopover } from './itinerary/CalendarView';
import { ListView } from './itinerary/ListView';
import { sortLegsChrono, updateActivityLeg, removeActivityLeg, updateFlightLeg, removeFlightLeg } from '../utils/scanDetails';

// Re-exported for existing consumers (SharedItineraryView, tests).
export { packItinerary, todBucket } from './itinerary/logic';

// An explicit "remind me to book" is the user asking for a todo — surface it
// in Bookings as soon as the idea is in the trip, regardless of whether it
// has a day slot yet (parking lot included). Curated ideas that merely carry
// a booking link (no explicit flag) stay gated to placed items only, so
// browsing the Ideas tab doesn't flood Bookings with maybes. selectedIds
// gates both cases so unpicked catalog ideas never show up.
export function computeBookableIdeas(allIdeas, selectedIds, placedIdeaIds, destination) {
  const selectedSet = new Set(selectedIds);
  const placedSet = placedIdeaIds instanceof Set ? placedIdeaIds : new Set(placedIdeaIds);
  // Ideas placed directly onto the itinerary no longer join selectedIds (see
  // addIdea/addCustomIdea below) — Ideas-pool membership and itinerary
  // placement are independent now, so either one qualifies here.
  return allIdeas.filter(i => (selectedSet.has(i.id) || placedSet.has(i.id)) && (i.requiresBooking || (bookingInfoForIdea(i, destination) && placedSet.has(i.id))));
}


export function ItineraryScreen({
  isMobile, trip, onBack, onBook, onDelete, onSavePlan, onSaveCustomIdea, onDeleteCustomIdea, onSaveOverrides, onSaveTripDetails,
  accent = PALETTE.accent, selectedIds, setSelectedIds,
  dayCount, setDayCount, destIdeas = [],
  bookings = {},
  onToggleBooked, onSaveDetails, onSavePacking,
  selectedPresets = [], setSelectedPresets,
  onSaveDates, onSaveLegs, onSaveAccommodation, onSaveDayNotes,
  onShare,
}) {
  const [showAdd, setShowAdd] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [shareUrl, setShareUrl] = React.useState(null);
  const [shareLoading, setShareLoading] = React.useState(false);
  const [localPlan, setLocalPlan] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('itinerary');
  const [bookingSheet, setBookingSheet] = React.useState(null);
  const [bookedDetailIdea, setBookedDetailIdea] = React.useState(null);
  const [legDetail, setLegDetail] = React.useState(null); // { kind: 'activity'|'flight', leg }
  const [showScan, setShowScan] = React.useState(false);
  const [showMap, setShowMap] = React.useState(false);
  const calModeKey = isMobile ? 'itinCalModeMobile' : 'itinCalMode';
  const [calMode, setCalMode] = React.useState(() => {
    try { return localStorage.getItem(calModeKey) || '3day'; } catch { return '3day'; }
  });
  const persistCalMode = (m) => { try { localStorage.setItem(calModeKey, m || ''); } catch {} };
  const [calDayOffset, setCalDayOffset] = React.useState(0);
  // Calendar (time grid) vs List (plain chronological agenda) — List reuses
  // every CalendarView handler as-is (see itinerary/ListView.js), added for
  // small screens where the grid is hard to read.
  const [viewMode, setViewMode] = React.useState(() => {
    try { return localStorage.getItem('itinViewMode') || 'calendar'; } catch { return 'calendar'; }
  });
  const persistViewMode = (m) => { setViewMode(m); try { localStorage.setItem('itinViewMode', m); } catch {} };
  // Calendar view settings (day start/end, block size) — lifted up from
  // CalendarView so the settings button can live in this toolbar next to
  // Map instead of eating its own row inside the calendar.
  const [calSettings, setCalSettings] = React.useState(loadCalSettings);
  const [showCalSettings, setShowCalSettings] = React.useState(false);
  const updateCalSettings = (patch) => {
    setCalSettings(prev => {
      const next = { ...prev, ...patch };
      if (next.endHour <= next.startHour) {
        if ('startHour' in patch) next.endHour = Math.min(24, next.startHour + 1);
        else next.startHour = Math.max(0, next.endHour - 1);
      }
      try { localStorage.setItem(CAL_SETTINGS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  React.useEffect(() => {
    if (!showCalSettings) return;
    const dismiss = (e) => { if (!e.target.closest('[data-cal-settings]')) setShowCalSettings(false); };
    window.addEventListener('pointerdown', dismiss);
    return () => window.removeEventListener('pointerdown', dismiss);
  }, [showCalSettings]);
  const [pendingCalTime, setPendingCalTime] = React.useState(null);

  const [localCustomIdeas, setLocalCustomIdeas] = React.useState(() => {
    const raw = trip?.customIdeas;
    return Array.isArray(raw) ? raw : Object.values(raw || {});
  });
  const [localOverrides, setLocalOverrides] = React.useState(
    () => trip?.ideaOverrides || {}
  );
  const [localAccommodations, setLocalAccommodations] = React.useState(
    () => trip?.accommodations || {}
  );
  const [localDayNotes, setLocalDayNotes] = React.useState(
    () => trip?.dayNotes || {}
  );

  // ── Undo stack ────────────────────────────────────────────────────────────────
  const undoStack = React.useRef([]);
  const handleUndoRef = React.useRef(null);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.contentEditable === 'true') return;
        e.preventDefault();
        handleUndoRef.current?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []); // eslint-disable-line

  React.useEffect(() => {
    const raw = trip?.customIdeas;
    setLocalCustomIdeas(Array.isArray(raw) ? raw : Object.values(raw || {}));
    setLocalOverrides(trip?.ideaOverrides || {});
    setLocalAccommodations(trip?.accommodations || {});
    setLocalDayNotes(trip?.dayNotes || {});
    const rawPlan = trip?.customPlan;
    let parsed = null;
    if (rawPlan) {
      try { parsed = typeof rawPlan === 'string' ? JSON.parse(rawPlan) : rawPlan; } catch {}
    }
    setLocalPlan(parsed);
    undoStack.current = [];
  }, [trip?.id]); // eslint-disable-line

  // See resizePlanRows (itinerary/logic.js) — resizing (not discarding)
  // localPlan on a dayCount change keeps parking-lot items unplaced.
  React.useEffect(() => {
    if (!localPlan || localPlan.length === dayCount) return;
    const resized = resizePlanRows(localPlan, dayCount);
    setLocalPlan(resized);
    onSavePlan?.(resized);
  }, [dayCount]); // eslint-disable-line

  const allIdeas = React.useMemo(() => {
    const base = [...localCustomIdeas, ...destIdeas];
    return base.map(idea => localOverrides[idea.id] ? { ...idea, ...localOverrides[idea.id] } : idea);
  }, [destIdeas, localCustomIdeas, localOverrides]);

  const legs = trip?.legs && trip.legs.length > 1 ? trip.legs : null;

  const { days: packedDays, overflow: packedOverflow } = React.useMemo(
    () => legs
      ? packMultiLegItinerary(legs, selectedIds, trip?.dateStart, allIdeas)
      : packItinerary(selectedIds, dayCount, trip?.dateStart, allIdeas),
    [selectedIds, dayCount, trip?.dateStart, legs, allIdeas]
  );

  const { displayDays, displayOverflow } = React.useMemo(() => {
    let baseDays, baseOverflow;
    if (!localPlan) {
      baseDays = packedDays;
      baseOverflow = packedOverflow;
    } else {
      // Plan rows render regardless of selectedIds — Ideas-pool membership
      // and itinerary placement are independent (see addIdea/addCustomIdea):
      // unselecting/removing an idea from the Ideas tab must not silently
      // yank it off the calendar if it's already placed on a day.
      const built = buildDaysFromPlan(localPlan, trip?.dateStart, dayCount, allIdeas, localOverrides, legs);
      const inPlan = new Set(localPlan.flat());
      baseDays = built;
      baseOverflow = selectedIds.filter(id => !inPlan.has(id));
    }
    // Re-sort each day's items by effective start time (including manual overrides)
    const sortedDays = baseDays.map(day => ({
      ...day,
      items: day.items.slice().sort((a, b) => {
        const aStart = localOverrides[a.idea.id]?.startTime ?? a.start;
        const bStart = localOverrides[b.idea.id]?.startTime ?? b.start;
        return aStart - bStart;
      }),
    }));
    return { displayDays: sortedDays, displayOverflow: baseOverflow };
  }, [localPlan, packedDays, packedOverflow, trip?.dateStart, dayCount, allIdeas, selectedIds, localOverrides, legs]);

  const placedIdeaIds = React.useMemo(
    () => new Set(displayDays.flatMap(d => d.items.map(s => s.idea.id))),
    [displayDays]
  );

  // Marks each day with its hotel's check-in/staying/check-out status,
  // sourced straight from whatever's been uploaded/typed into the Bookings
  // tab (bookings.hotel_details, or bookings.hotel_<i>_details per leg on
  // multi-city trips) — no separate manual re-entry needed.
  const hotelByDay = React.useMemo(() => {
    if (!trip?.dateStart) return {};
    const base = new Date(trip.dateStart + 'T12:00:00');
    const result = {};
    const applyHotel = (hd, fallbackCheckIn, fallbackCheckOut) => {
      if (!hd?.hotelName) return;
      const checkInIso = hd.checkIn || fallbackCheckIn;
      if (!checkInIso) return;
      const checkIn = new Date(checkInIso + 'T12:00:00');
      const checkOutIso = hd.checkOut || fallbackCheckOut;
      const checkOut = checkOutIso ? new Date(checkOutIso + 'T12:00:00') : null;
      displayDays.forEach((_, di) => {
        const d = new Date(base); d.setDate(base.getDate() + di);
        const isIn = d.toDateString() === checkIn.toDateString();
        const isOut = checkOut && d.toDateString() === checkOut.toDateString();
        const during = d > checkIn && (!checkOut || d < checkOut);
        if (isIn || isOut || during) result[di] = { name: hd.hotelName, tag: isIn ? 'Check-in' : isOut ? 'Check-out' : 'Staying' };
      });
    };

    const ranges = legDateRanges(trip);
    const bk = trip?.bookings || {};
    if (ranges) {
      ranges.forEach((r, i) => applyHotel(bk[`hotel_${i}_details`], r.dateStart, r.dateEnd));
    } else {
      applyHotel(bk.hotel_details, null, null);
    }
    // Current model: any number of stays live in bookings.accommodation_details
    // (added via "Add a hotel booking" on the Bookings tab) — each one is
    // self-contained, so this is what makes a newly-added stay show up here
    // in the itinerary automatically, with no separate per-day re-entry.
    (bk['accommodation_details']?.legs || []).forEach(leg => applyHotel(leg, null, null));
    return result;
  }, [trip, displayDays]);

  const accomBookedByDay = React.useMemo(() => {
    const namedDays = Array.from({ length: dayCount }, (_, i) => {
      const a = localAccommodations[i];
      return { dayIdx: i, name: a?.name?.trim() || '' };
    }).filter(d => d.name);
    const groups = [];
    for (const day of namedDays) {
      const prev = groups[groups.length - 1];
      if (prev && prev.name === day.name && prev.endIdx === day.dayIdx - 1) {
        prev.endIdx = day.dayIdx;
      } else {
        groups.push({ name: day.name, startIdx: day.dayIdx, endIdx: day.dayIdx });
      }
    }
    const result = {};
    for (const g of groups) {
      const isBooked = !!bookings[`accom_${g.startIdx}`];
      for (let i = g.startIdx; i <= g.endIdx; i++) result[i] = isBooked;
    }
    // Stays added via "Add a hotel booking" share one checked toggle
    // (bookings.accommodation), same pattern as flights — any day already
    // covered by hotelByDay inherits that same booked state.
    const accomChecked = !!bookings['accommodation'];
    for (const dayIdx of Object.keys(hotelByDay)) result[dayIdx] = result[dayIdx] || accomChecked;
    return result;
  }, [localAccommodations, dayCount, bookings, hotelByDay]);

  // What the itinerary's calendar/outlook footer displays per day — a stay
  // added via "Add a hotel booking" (hotelByDay) takes precedence over the
  // older manually-typed per-day name, so booking a hotel is enough on its
  // own; no separate re-entry in the itinerary is needed to see it there.
  //
  // Checkout day is excluded: a hotel with checkOut=14/09 means the last
  // night slept there was 13/09, checking out the morning of 14/09 — that
  // date is NOT a night spent at the hotel, so it shouldn't show up as
  // "tonight's accommodation" (that's still shown, correctly tagged
  // "Check-out", on the separate top-of-day hotel banner via hotelByDay
  // directly). If a different stay checks in the same day, that later
  // hotelByDay entry overwrites this one with tag 'Check-in', so the
  // transition day still correctly shows the new hotel.
  const calendarAccommodations = React.useMemo(() => {
    const merged = { ...localAccommodations };
    for (const [dayIdx, hd] of Object.entries(hotelByDay)) {
      if (hd.tag === 'Check-out') continue;
      merged[dayIdx] = { name: hd.name };
    }
    return merged;
  }, [localAccommodations, hotelByDay]);

  // Maps day index → array of flight bands landing on that day. Flights
  // aren't forced into "outbound/return" — a multi-city trip (SIN→BCN,
  // BCN→BRU, BRU→SIN) is just 3 legs, none of which fits that binary — so
  // each band is labeled by its actual route and colored by its position in
  // the trip's chronological flight order (see utils/scanDetails.js).
  const flightByDay = React.useMemo(() => {
    const legs = bookings['flights_details']?.legs || [];
    if (legs.length === 0) return {};
    const last = displayDays.length - 1;

    // Compute day index from a date string relative to trip start, clamped
    // to [0, last]. A date so far outside the trip's window that it can
    // only be bad data (e.g. a scan mangling the year) returns null rather
    // than getting silently clamped onto day 1/last — showing a flight on
    // the wrong day is worse than not showing it at all.
    const dateToIdx = (dateStr) => {
      if (!dateStr || !trip?.dateStart) return 0;
      const tripMs = new Date(trip.dateStart + 'T12:00:00').getTime();
      const flightMs = new Date(dateStr + 'T12:00:00').getTime();
      if (Number.isNaN(flightMs)) return 0;
      const offset = Math.round((flightMs - tripMs) / 86400000);
      if (offset < -2 || offset > last + 2) return null;
      return Math.max(0, Math.min(last, offset));
    };

    const sortedLegs = sortLegsChrono(legs);
    const result = {};
    sortedLegs.forEach((leg, seq) => {
      const idx = dateToIdx(leg.date);
      if (idx === null) return;
      const band = {
        id: leg.id, seq, airline: leg.airline,
        flightNo: leg.flightNo, date: leg.date,
        depTime: leg.depTime, arrTime: leg.arrTime,
        origin: leg.origin, dest: leg.dest,
      };
      (result[idx] = result[idx] || []).push(band);
    });
    Object.values(result).forEach(bands => bands.sort((a, b) => (a.depTime || '').localeCompare(b.depTime || '')));
    return result;
  }, [bookings, displayDays.length, trip?.dateStart]);

  // Maps day index → array of booked-activity legs landing on that day, same
  // dateToIdx pattern as flightByDay. Activities filed without a date (a
  // scan/manual entry can be left blank) simply don't appear here — there's
  // no day to place them on.
  const activityByDay = React.useMemo(() => {
    const legs = bookings['activities_details']?.legs || [];
    if (legs.length === 0 || !trip?.dateStart) return {};
    const last = displayDays.length - 1;
    const tripMs = new Date(trip.dateStart + 'T12:00:00').getTime();
    const dateToIdx = (dateStr) => {
      if (!dateStr) return null;
      const actMs = new Date(dateStr + 'T12:00:00').getTime();
      if (Number.isNaN(actMs)) return null;
      const offset = Math.round((actMs - tripMs) / 86400000);
      if (offset < 0 || offset > last) return null;
      return offset;
    };
    const result = {};
    legs.forEach(leg => {
      const idx = dateToIdx(leg.date);
      if (idx === null) return;
      (result[idx] = result[idx] || []).push(leg);
    });
    Object.values(result).forEach(arr => arr.sort((a, b) => (a.time || '').localeCompare(b.time || '')));
    return result;
  }, [bookings, displayDays.length, trip?.dateStart]);

  const saveAccommodation = (dayIdx, data) => {
    const updated = { ...localAccommodations, [dayIdx]: data };
    setLocalAccommodations(updated);
    onSaveAccommodation?.(updated);
  };

  const saveDayNote = (dayIdx, text) => {
    const updated = { ...localDayNotes, [dayIdx]: text };
    setLocalDayNotes(updated);
    onSaveDayNotes?.(updated);
  };

  // Clicking a flight/activity band on the Calendar opens LegDetailSheet.
  // CalendarView's own flightByDay band objects are a stripped-down subset
  // (route/times only, no confirmation/notes/screenshot) built for compact
  // rendering — look the full leg back up by id here so editing/saving from
  // the popup can never clobber fields the band didn't carry.
  const openLegDetail = (kind, bandOrLeg) => {
    const detailsKey = kind === 'activity' ? 'activities_details' : 'flights_details';
    const legs = bookings?.[detailsKey]?.legs || [];
    const fullLeg = legs.find(l => l.id === bandOrLeg.id) || bandOrLeg;
    setLegDetail({ kind, leg: fullLeg });
  };

  const saveLegDetail = (kind, updatedLeg) => {
    const detailsKey = kind === 'activity' ? 'activities_details' : 'flights_details';
    const existing = bookings?.[detailsKey] || {};
    const patch = kind === 'activity'
      ? updateActivityLeg(existing, updatedLeg.id, updatedLeg)
      : updateFlightLeg(existing, updatedLeg.id, updatedLeg);
    onSaveDetails?.(kind === 'activity' ? 'activities' : 'flights', patch);
    setLegDetail(d => (d ? { ...d, leg: updatedLeg } : d));
  };

  const removeLegDetail = (kind, legId) => {
    const detailsKey = kind === 'activity' ? 'activities_details' : 'flights_details';
    const existing = bookings?.[detailsKey] || {};
    const patch = kind === 'activity' ? removeActivityLeg(existing, legId) : removeFlightLeg(existing, legId);
    onSaveDetails?.(kind === 'activity' ? 'activities' : 'flights', patch);
  };

  const getCurrentPlan = () =>
    localPlan ? localPlan.map(d => [...d]) : displayDays.map(d => d.items.map(s => s.idea.id));

  const resequenceDayTimes = (newPlan, affectedDayIdxs, overrides) => {
    let newOverrides = { ...overrides };
    for (const dayIdx of affectedDayIdxs) {
      const dayItems = displayDays[dayIdx]?.items || [];
      // Anchor at the earliest current effective start in this day (preserve day start time)
      let anchor = 9;
      if (dayItems.length > 0) {
        anchor = Math.min(...dayItems.map(s => overrides[s.idea.id]?.startTime ?? s.start));
      }
      const newOrder = newPlan[dayIdx] || [];
      let t = anchor;
      for (const id of newOrder) {
        const idea = allIdeas.find(i => i.id === id);
        if (!idea) continue;
        const dur = newOverrides[id]?.dur ?? idea.dur;
        if (newOverrides[id]?.fixedTime && newOverrides[id]?.startTime !== undefined) {
          // Pinned item: keep its time, advance running clock past it
          t = newOverrides[id].startTime + dur;
        } else {
          newOverrides[id] = { ...(newOverrides[id] || {}), startTime: t };
          t += dur;
        }
      }
    }
    return newOverrides;
  };

  // Snapshot current mutable state onto the undo stack before mutations.
  const pushUndo = () => {
    undoStack.current.push({
      localPlan: localPlan ? localPlan.map(d => [...d]) : null,
      localOverrides: { ...localOverrides },
      selectedIds: [...selectedIds],
      localCustomIdeas: [...localCustomIdeas],
    });
    if (undoStack.current.length > 50) undoStack.current.shift();
  };

  // Keep handleUndoRef fresh every render so the keyboard listener always has current state.
  handleUndoRef.current = () => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    setLocalPlan(prev.localPlan);
    setLocalOverrides(prev.localOverrides);
    setSelectedIds(prev.selectedIds);
    setLocalCustomIdeas(prev.localCustomIdeas);
    onSavePlan?.(prev.localPlan);
    onSaveOverrides?.(prev.localOverrides);
  };

  const removeIdea = (id) => {
    pushUndo();
    setSelectedIds(selectedIds.filter(x => x !== id));
    if (localPlan) {
      const plan = localPlan.map(d => d.filter(x => x !== id));
      setLocalPlan(plan);
      onSavePlan?.(plan);
    }
  };

  const moveToDay = (ideaId, toDayIdx) => {
    pushUndo();
    const plan = getCurrentPlan();
    // Remove from whichever day currently has it
    let fromDayIdx = -1;
    for (let d = 0; d < plan.length; d++) {
      const idx = plan[d].indexOf(ideaId);
      if (idx !== -1) { fromDayIdx = d; plan[d].splice(idx, 1); break; }
    }
    plan[toDayIdx].push(ideaId);
    setLocalPlan(plan);
    onSavePlan?.(plan);
    const affected = fromDayIdx !== -1 && fromDayIdx !== toDayIdx ? [fromDayIdx, toDayIdx] : [toDayIdx];
    const newOv = resequenceDayTimes(plan, affected, localOverrides);
    setLocalOverrides(newOv);
    onSaveOverrides?.(newOv);
  };

  const moveToDayAtTime = (ideaId, toDayIdx, startTime) => {
    pushUndo();
    const plan = getCurrentPlan();
    let fromDayIdx = -1;
    for (let d = 0; d < plan.length; d++) {
      const idx = plan[d].indexOf(ideaId);
      if (idx !== -1) { fromDayIdx = d; plan[d].splice(idx, 1); break; }
    }
    plan[toDayIdx].push(ideaId);
    setLocalPlan(plan);
    onSavePlan?.(plan);
    const affected = fromDayIdx !== -1 && fromDayIdx !== toDayIdx ? [fromDayIdx, toDayIdx] : [toDayIdx];
    let newOv = resequenceDayTimes(plan, affected, localOverrides);
    newOv = { ...newOv, [ideaId]: { ...(newOv[ideaId] || {}), startTime, fixedTime: true } };
    setLocalOverrides(newOv);
    onSaveOverrides?.(newOv);
  };

  const moveToParking = (id) => {
    pushUndo();
    if (localPlan === null) {
      const plan = displayDays.map(day => day.items.map(slot => slot.idea.id)).map(d => d.filter(x => x !== id));
      setLocalPlan(plan);
      onSavePlan?.(plan);
    } else {
      const plan = localPlan.map(d => d.filter(x => x !== id));
      setLocalPlan(plan);
      onSavePlan?.(plan);
    }
    // selectedIds unchanged — idea remains in overflow/parking lot
  };

  // Compute effective end-of-day time (max end across all items, respecting overrides)
  const getLastEndOfDay = (dayIdx) => {
    const items = displayDays[dayIdx]?.items || [];
    if (items.length === 0) return 9;
    let maxEnd = 9;
    for (const slot of items) {
      const start = timeOverrides[slot.idea.id] ?? slot.start;
      const dur = localOverrides[slot.idea.id]?.dur ?? slot.idea.dur;
      maxEnd = Math.max(maxEnd, start + dur);
    }
    return maxEnd;
  };

  const addIdea = (id, dayIdx, extraOverride = null) => {
    pushUndo();
    // Placing an idea straight onto a day (from the day's own AddSheet) does
    // NOT add it to selectedIds — the Ideas tab is a separate candidate pool,
    // not a mirror of what's on the calendar. An idea promoted here from the
    // parking lot is already selected, so this is a no-op for that path.
    let newPlan = null;
    if (localPlan !== null) {
      newPlan = localPlan.map(d => [...d]);
      newPlan[dayIdx != null ? dayIdx : newPlan.length - 1].push(id);
    } else if (dayIdx != null) {
      // In auto mode with an explicit day target — freeze layout and assign to that day
      newPlan = displayDays.map(day => day.items.map(slot => slot.idea.id));
      newPlan[dayIdx].push(id);
    }
    if (newPlan) {
      setLocalPlan(newPlan);
      onSavePlan?.(newPlan);
    }
    // Resequence the affected day so the new item flows correctly around any fixed-time items
    if (dayIdx != null && newPlan) {
      let newOv = resequenceDayTimes(newPlan, [dayIdx], localOverrides);
      if (extraOverride) newOv = { ...newOv, [id]: { ...(newOv[id] || {}), ...extraOverride } };
      setLocalOverrides(newOv);
      onSaveOverrides?.(newOv);
    }
  };

  const addCustomIdeaToPool = (idea) => {
    pushUndo();
    setLocalCustomIdeas(prev => [idea, ...prev]);
    if (!selectedIds.includes(idea.id)) setSelectedIds([...selectedIds, idea.id]);
    if (localPlan === null) {
      const frozenPlan = displayDays.map(day => day.items.map(slot => slot.idea.id));
      setLocalPlan(frozenPlan);
      onSavePlan?.(frozenPlan);
    }
    onSaveCustomIdea?.(idea);
  };

  const editCustomIdea = (updatedIdea) => {
    setLocalCustomIdeas(prev => prev.map(i => i.id === updatedIdea.id ? updatedIdea : i));
    onSaveCustomIdea?.(updatedIdea);
  };

  // Full delete — removes the custom idea's data and strips it out of the
  // itinerary and the Ideas pool alike. Used by EditItemSheet's "Remove from
  // trip", which is explicitly about the item itself, not just its Ideas-tab
  // candidacy.
  const deleteCustomIdea = (id) => {
    pushUndo();
    setLocalCustomIdeas(prev => prev.filter(i => i.id !== id));
    setSelectedIds(selectedIds.filter(x => x !== id));
    if (localPlan) {
      const plan = localPlan.map(d => d.filter(pid => pid !== id));
      setLocalPlan(plan);
      onSavePlan?.(plan);
    }
    onDeleteCustomIdea?.(id);
  };

  // The Ideas tab's own "x" — removes the idea from the Ideas pool only.
  // If it's already placed on a day, the itinerary keeps it (and the idea's
  // data) intact; only a genuinely unplaced idea's data is actually deleted,
  // since it would otherwise linger as orphaned junk nobody can reach.
  const removeCustomIdeaFromPool = (id) => {
    pushUndo();
    const placed = !!localPlan && localPlan.some(day => day.includes(id));
    setSelectedIds(selectedIds.filter(x => x !== id));
    if (!placed) {
      setLocalCustomIdeas(prev => prev.filter(i => i.id !== id));
      onDeleteCustomIdea?.(id);
    }
  };

  const addCustomIdea = (idea, dayIdx, extraOverride = null) => {
    pushUndo();
    setLocalCustomIdeas(prev => [idea, ...prev]);
    // Unlike addCustomIdeaToPool (the Ideas-tab bar), this comes from a
    // day's own AddSheet — it should land only on the itinerary, not also
    // clutter the separate Ideas candidate pool.
    if (localPlan !== null) {
      const plan = localPlan.map(d => [...d]);
      plan[dayIdx != null ? dayIdx : plan.length - 1].push(idea.id);
      setLocalPlan(plan);
      onSavePlan?.(plan);
    } else {
      // AddSheet always targets a specific day — freeze layout and assign
      const frozen = displayDays.map(day => day.items.map(slot => slot.idea.id));
      const targetDay = dayIdx != null ? dayIdx : frozen.length - 1;
      frozen[targetDay].push(idea.id);
      setLocalPlan(frozen);
      onSavePlan?.(frozen);
    }
    onSaveCustomIdea?.(idea);
    // Apply time override or auto-suggest start time
    if (dayIdx != null && displayDays[dayIdx]) {
      const suggestedStart = extraOverride?.startTime ?? getLastEndOfDay(dayIdx);
      const base = { ...(localOverrides[idea.id] || {}), startTime: suggestedStart };
      if (extraOverride) Object.assign(base, extraOverride);
      const newOverrides = { ...localOverrides, [idea.id]: base };
      setLocalOverrides(newOverrides);
      onSaveOverrides?.(newOverrides);
    }
  };

  const handleSaveItemEdit = (id, changes) => {
    pushUndo();
    const newOverrides = { ...localOverrides, [id]: { ...(localOverrides[id] || {}), ...changes } };
    setLocalOverrides(newOverrides);
    onSaveOverrides?.(newOverrides);
  };

  const handleEditTime = (id, startHour) => {
    pushUndo();
    const newOverrides = { ...localOverrides, [id]: { ...(localOverrides[id] || {}), startTime: startHour } };
    // When a pinned item's time changes, sort the plan day and resequence neighbors
    if (localOverrides[id]?.fixedTime) {
      const dayIdx = displayDays.findIndex(day => day.items.some(s => s.idea.id === id));
      if (dayIdx !== -1) {
        const plan = getCurrentPlan();
        plan[dayIdx] = plan[dayIdx].slice().sort((a, b) => {
          const getTime = (itemId) => {
            if (itemId === id) return startHour;
            if (newOverrides[itemId]?.fixedTime && newOverrides[itemId].startTime !== undefined) return newOverrides[itemId].startTime;
            return localOverrides[itemId]?.startTime ?? (displayDays[dayIdx]?.items.find(s => s.idea.id === itemId)?.start ?? 9);
          };
          return getTime(a) - getTime(b);
        });
        const finalOv = resequenceDayTimes(plan, [dayIdx], newOverrides);
        setLocalPlan(plan);
        onSavePlan?.(plan);
        setLocalOverrides(finalOv);
        onSaveOverrides?.(finalOv);
        return;
      }
    }
    setLocalOverrides(newOverrides);
    onSaveOverrides?.(newOverrides);
  };

  const handleEditDuration = (id, dur) => {
    pushUndo();
    const newOverrides = { ...localOverrides, [id]: { ...(localOverrides[id] || {}), dur } };
    setLocalOverrides(newOverrides);
    onSaveOverrides?.(newOverrides);
  };

  // Confirmed is the default (undefined/false); tentative is the explicit
  // opt-in flag, so existing trips don't retroactively turn dotted.
  const handleToggleConfirmed = (id) => {
    pushUndo();
    const cur = localOverrides[id];
    const newOverrides = { ...localOverrides, [id]: { ...(cur || {}), tentative: !cur?.tentative } };
    setLocalOverrides(newOverrides);
    onSaveOverrides?.(newOverrides);
  };

  const timeOverrides = React.useMemo(() => {
    const r = {};
    for (const [id, ov] of Object.entries(localOverrides)) {
      if (ov?.startTime !== undefined) r[id] = ov.startTime;
    }
    return r;
  }, [localOverrides]);

  const editingItemStart = React.useMemo(() => {
    if (!editingItem) return null;
    if (timeOverrides[editingItem.id] !== undefined) return timeOverrides[editingItem.id];
    for (const day of displayDays) {
      const slot = day.items.find(s => s.idea.id === editingItem.id);
      if (slot) return slot.start;
    }
    return null;
  }, [editingItem, timeOverrides, displayDays]);

  const topPad = headerPad(isMobile);
  const px = isMobile ? 20 : 28;
  const tripLabel = getTripLabel(trip);

  const ITINERARY_TABS = [
    { id: 'dates', label: 'Dates' },
    { id: 'itinerary', label: 'Itinerary' },
    { id: 'ideas', label: 'Ideas' },
    { id: 'presets', label: 'Presets' },
    { id: 'bookings', label: 'Bookings' },
    { id: 'scan', label: 'Scan' },
    { id: 'settings', label: 'Settings' },
  ];

  const handleTabClick = (tab) => {
    if (tab === 'scan') { setShowScan(true); return; }
    setActiveTab(tab);
  };

  return (
    <div style={{ background: PALETTE.paper, minHeight: '100vh' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 4, background: PALETTE.paper,
        padding: topPad, display: 'flex', flexDirection: 'column', gap: 4,
        borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36 }}>
          <IconBtn name="arrowL" label="Back" onClick={onBack} />
          {tripLabel && (
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
              {tripLabel}
            </div>
          )}
          {onShare && (
            <button
              onClick={async () => {
                if (shareUrl) { setShareUrl(null); return; }
                setShareLoading(true);
                try {
                  const url = await onShare();
                  setShareUrl(url);
                } finally { setShareLoading(false); }
              }}
              title="Share itinerary"
              style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: shareUrl ? accent : PALETTE.inkMute, padding: '4px 6px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              {shareLoading
                ? <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${PALETTE.accent}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                : <Icon name="share" size={18} />}
            </button>
          )}
        </div>
        {shareUrl && (
          <div style={{ margin: '4px 0', padding: '8px 12px', background: PALETTE.paperDim, borderRadius: 10, border: `1px solid ${PALETTE.rule}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shareUrl}</div>
              <button
                onClick={() => { navigator.clipboard?.writeText(shareUrl); }}
                style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: PALETTE.paper, cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, fontWeight: 600, color: PALETTE.ink, padding: '4px 10px', borderRadius: 6, flexShrink: 0 }}
              >Copy</button>
            </div>
            {/* Guests self-identify by name when they open the link — read-only,
                just so you can see who's actually looked at it. */}
            {Object.keys(trip?.shareGuests || {}).length > 0 && (
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>👥</span>
                <span>
                  Viewed by: {Object.values(trip.shareGuests)
                    .sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0))
                    .map(g => g.name).join(', ')}
                </span>
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', marginTop: 4 }}>
          {ITINERARY_TABS.map(({ id, label }) => {
            const active = activeTab === id;
            return (
              <button key={id} onClick={() => handleTabClick(id)} style={{
                flexShrink: 0, padding: '7px 14px', cursor: 'pointer',
                fontFamily: 'Inter', fontSize: 12, fontWeight: active ? 600 : 400,
                color: active ? PALETTE.ink : PALETTE.inkMute,
                background: 'none', border: 'none',
                borderBottom: active ? `2px solid ${PALETTE.ink}` : `2px solid ${PALETTE.rule}`,
                transition: 'all 0.15s',
              }}>{label}</button>
            );
          })}
        </div>
      </div>

      {activeTab === 'dates' && (
        <DatesPanel trip={trip} onSaveDates={onSaveDates} onSaveLegs={onSaveLegs} accent={accent} px={px} />
      )}

      {activeTab === 'ideas' && (
        <IdeasPanel
          selectedIds={selectedIds} setSelectedIds={setSelectedIds}
          allIdeas={allIdeas} accent={accent} px={px}
          onAddCustom={addCustomIdeaToPool}
          customIdeaIds={new Set(localCustomIdeas.map(i => i.id))}
          onEditCustom={editCustomIdea}
          onDeleteCustom={removeCustomIdeaFromPool}
          overflowIds={new Set(displayOverflow)}
          placedIdeaIds={placedIdeaIds}
          legs={legs}
          bookings={bookings}
          destination={trip?.destination}
        />
      )}

      {activeTab === 'presets' && (
        <PresetsPanel
          selectedPresets={selectedPresets} setSelectedPresets={setSelectedPresets}
          destIdeas={destIdeas} allIdeas={allIdeas}
          selectedIds={selectedIds} setSelectedIds={setSelectedIds}
          localPlan={localPlan} setLocalPlan={setLocalPlan}
          onSavePlan={onSavePlan} dayCount={dayCount}
          displayDays={displayDays} trip={trip}
          accent={accent} px={px}
          legs={legs}
        />
      )}

      {activeTab === 'itinerary' ? (
        <>
          <div style={{ margin: `4px ${px}px 12px` }}>
            <IdeaBar onAdd={addCustomIdeaToPool} accent={accent} legs={legs} />
          </div>

          <div style={{
            margin: `0 ${px}px 20px`, padding: '10px 14px',
            background: PALETTE.card, borderRadius: 12, border: `0.5px solid ${PALETTE.ruleSoft}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkSoft, letterSpacing: 0.5,
          }}>
            {/* placedIdeaIds, not selectedIds.length - displayOverflow.length —
                an idea added straight to a day never joins selectedIds (see
                addCustomIdea/addIdea), so that arithmetic undercounted what's
                actually on the calendar. */}
            <span>{displayDays.length} days · {placedIdeaIds.size} placed</span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {localPlan && (
                <button onClick={() => {
                  pushUndo();
                  if (displayOverflow.length > 0) {
                    const dayCaps = Array.from({ length: dayCount }, (_, di) => {
                      const bands = flightByDay[di] || [];
                      let cap = 10;
                      for (const b of bands) {
                        const dep = parseFlightHM(b.depTime);
                        const arr = parseFlightHM(b.arrTime);
                        // Any flight landing that day: activities can only start after landing
                        if (arr !== null) cap = Math.min(cap, Math.max(0, 19 - arr));
                        // Any flight departing that day: activities must end before heading to the airport
                        if (dep !== null) cap = Math.min(cap, Math.max(0, dep - 9));
                      }
                      return cap;
                    });
                    const filled = fillEmptySlots(localPlan, selectedIds, allIdeas, localOverrides, dayCaps);
                    setLocalPlan(filled);
                    onSavePlan?.(filled);
                  }
                }} style={{
                  appearance: 'none', border: 0, background: 'transparent',
                  fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.8,
                  textTransform: 'uppercase', color: displayOverflow.length > 0 ? PALETTE.inkMute : PALETTE.rule,
                  cursor: displayOverflow.length > 0 ? 'pointer' : 'default', padding: 0,
                }}>Auto-fill</button>
              )}
              {displayOverflow.length > 0 && <span style={{ color: PALETTE.inkMute }}>{displayOverflow.length} in parking lot</span>}
              <div style={{ display: 'flex', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, overflow: 'hidden' }}>
                {[['calendar','Cal'],['list','List']].map(([mode, label]) => (
                  <button
                    key={mode}
                    onClick={() => persistViewMode(mode)}
                    style={{ appearance: 'none', border: 0, cursor: 'pointer', padding: isMobile ? '6px 10px' : '4px 8px', background: viewMode === mode ? (isMobile ? PALETTE.ink : accent) : 'transparent', color: viewMode === mode ? (isMobile ? PALETTE.paper : '#fff') : PALETTE.inkMute, fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.3 }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {viewMode === 'calendar' && isMobile && (
                <div style={{ display: 'flex', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, overflow: 'hidden' }}>
                  {[['day','1D'],['3day','3D'],['all','All']].map(([mode, label]) => (
                    <button
                      key={mode}
                      onClick={() => { setCalMode(mode); setCalDayOffset(0); persistCalMode(mode); }}
                      style={{ appearance: 'none', border: 0, cursor: 'pointer', padding: '6px 10px', background: calMode === mode ? PALETTE.ink : 'transparent', color: calMode === mode ? PALETTE.paper : PALETTE.inkMute, fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.3 }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {viewMode === 'calendar' && !isMobile && (
                <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <div style={{ display: 'flex', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, overflow: 'hidden' }}>
                    {[['day','1D'],['3day','3D'],['all','All']].map(([mode, label]) => (
                      <button key={mode} onClick={() => { setCalMode(mode); setCalDayOffset(0); persistCalMode(mode); }} style={{ appearance: 'none', border: 0, cursor: 'pointer', padding: '4px 8px', background: calMode === mode ? accent : 'transparent', color: calMode === mode ? '#fff' : PALETTE.inkMute, fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.3 }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${!['day','3day','all'].includes(calMode) && !isNaN(parseInt(calMode,10)) ? accent : PALETTE.rule}`, borderRadius: 6, padding: '2px 5px', background: !['day','3day','all'].includes(calMode) && !isNaN(parseInt(calMode,10)) ? accent : 'transparent' }}>
                    <input
                      type="number" min={1} max={displayDays.length}
                      value={!['day','3day','all'].includes(calMode) && !isNaN(parseInt(calMode,10)) ? parseInt(calMode,10) : ''}
                      onChange={e => {
                        const n = parseInt(e.target.value, 10);
                        if (!isNaN(n) && n >= 1) { const m = String(n); setCalMode(m); setCalDayOffset(0); persistCalMode(m); }
                      }}
                      placeholder="N"
                      style={{ width: 22, border: 0, background: 'transparent', fontFamily: 'JetBrains Mono', fontSize: 9, color: !['day','3day','all'].includes(calMode) && !isNaN(parseInt(calMode,10)) ? '#fff' : PALETTE.inkMute, padding: 0, textAlign: 'center', appearance: 'textfield', MozAppearance: 'textfield', outline: 'none' }}
                    />
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: !['day','3day','all'].includes(calMode) && !isNaN(parseInt(calMode,10)) ? '#fff' : PALETTE.inkMute }}>D</span>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {viewMode === 'calendar' && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onClick={() => setShowCalSettings(v => !v)}
                      aria-label="Calendar view settings"
                      data-cal-settings="1"
                      style={{ appearance: 'none', border: `1px solid ${showCalSettings ? accent : PALETTE.rule}`, background: showCalSettings ? `${accent}12` : 'transparent', color: showCalSettings ? accent : PALETTE.inkMute, cursor: 'pointer', padding: '4px 7px', borderRadius: 6, display: 'flex', alignItems: 'center' }}
                    >
                      <Icon name="settings" size={11} />
                    </button>
                    {showCalSettings && (
                      <CalendarSettingsPopover settings={calSettings} onChange={updateCalSettings} onClose={() => setShowCalSettings(false)} />
                    )}
                  </div>
                )}
                <button
                  onClick={() => setShowMap(true)}
                  title="Map view"
                  style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', color: PALETTE.inkMute, cursor: 'pointer', padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.5, flexShrink: 0 }}
                >
                  <Icon name="pin" size={11} /> Map
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'calendar' && isMobile && (
            <div style={{ margin: `0 ${px}px 12px`, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="menu" size={13} />
              Hold to move · tap to edit · swipe for more days
            </div>
          )}

          {viewMode === 'list' ? (
            <ListView
              displayDays={displayDays}
              localOverrides={localOverrides}
              onEditItem={(idea) => setEditingItem(idea)}
              onMoveToParking={moveToParking}
              onRemoveFromTrip={removeIdea}
              onMoveToDay={moveToDay}
              flightByDay={flightByDay}
              activityByDay={activityByDay}
              accommodations={calendarAccommodations}
              accomBookedByDay={accomBookedByDay}
              dayNotes={localDayNotes}
              onOpenLeg={openLegDetail}
              displayOverflow={displayOverflow}
              allIdeas={allIdeas}
            />
          ) : (
          <CalendarView
              displayDays={displayDays}
              accent={accent}
              localOverrides={localOverrides}
              onEditTime={handleEditTime}
              onAddAtTime={(dayIdx, startHour, dur) => {
                setPendingCalTime({ dayIdx, startHour, dur });
                setShowAdd(dayIdx);
              }}
              onEditItem={(idea) => setEditingItem(idea)}
              onMoveToDayAtTime={moveToDayAtTime}
              onMoveToParking={moveToParking}
              onRemoveFromTrip={removeIdea}
              onPlaceFromParking={(id, dayIdx, h) => addIdea(id, dayIdx, { startTime: h, fixedTime: true })}
              onEditDuration={handleEditDuration}
              onToggleConfirmed={handleToggleConfirmed}
              onResizeTop={(id, newStart, newDur) => {
                pushUndo();
                const newOv = { ...localOverrides, [id]: { ...(localOverrides[id] || {}), startTime: newStart, dur: newDur, fixedTime: true } };
                setLocalOverrides(newOv); onSaveOverrides?.(newOv);
              }}
              displayOverflow={displayOverflow}
              allIdeas={allIdeas}
              flightByDay={flightByDay}
              activityByDay={activityByDay}
              accommodations={calendarAccommodations}
              accomBookedByDay={accomBookedByDay}
              bookings={bookings}
              calMode={calMode}
              calDayOffset={calDayOffset}
              setCalDayOffset={setCalDayOffset}
              destination={trip?.destination}
              onOpenLeg={openLegDetail}
              calSettings={calSettings}
              dayNotes={localDayNotes}
              onSaveAccommodation={saveAccommodation}
              onSaveDayNote={saveDayNote}
            />
          )}

          {showAdd !== false && (
            <AddSheet
              dayIdx={showAdd} selectedIds={selectedIds} allIdeas={allIdeas} legs={legs}
              onAdd={(id, extra = {}) => {
                if (pendingCalTime !== null) {
                  const { dayIdx: pDay, startHour, dur: pDur } = pendingCalTime;
                  addIdea(id, pDay, { startTime: startHour, fixedTime: true, ...(pDur != null ? { dur: pDur } : {}), ...extra });
                } else {
                  addIdea(id, showAdd, extra);
                }
                setPendingCalTime(null);
                setShowAdd(false);
              }}
              onAddCustom={(idea, extra = {}) => {
                // idea.dur already reflects any edit made in the sheet, so
                // (unlike onAdd's existing-catalog-idea path) duration never
                // comes from pendingCalTime here — only the day target does.
                const targetDay = pendingCalTime !== null ? pendingCalTime.dayIdx : showAdd;
                addCustomIdea(idea, targetDay, { fixedTime: true, ...extra });
                setPendingCalTime(null);
                setShowAdd(false);
              }}
              onSaveBookingDetails={onSaveDetails}
              onClose={() => { setPendingCalTime(null); setShowAdd(false); }} accent={accent}
              initialStartHour={pendingCalTime?.dayIdx === showAdd ? pendingCalTime.startHour : null}
              initialDur={pendingCalTime?.dayIdx === showAdd ? pendingCalTime.dur : null}
            />
          )}

          {editingItem && (
            <EditItemSheet
              idea={editingItem}
              isCustom={new Set(localCustomIdeas.map(i => i.id)).has(editingItem.id)}
              startTime={editingItemStart}
              onSaveTime={editingItemStart !== null ? (h) => handleEditTime(editingItem.id, h) : undefined}
              onSave={(changes) => handleSaveItemEdit(editingItem.id, changes)}
              onDelete={() => {
                if (new Set(localCustomIdeas.map(i => i.id)).has(editingItem.id)) deleteCustomIdea(editingItem.id);
                else removeIdea(editingItem.id);
              }}
              onClose={() => setEditingItem(null)}
              accent={accent}
              tentative={!!localOverrides[editingItem.id]?.tentative}
              onToggleConfirmed={() => handleToggleConfirmed(editingItem.id)}
            />
          )}
        </>
      ) : null}

      {activeTab === 'bookings' && (
        <BookingScreen
          embedded
          isMobile={isMobile}
          trip={trip}
          onToggleBooked={onToggleBooked}
          onSaveDetails={onSaveDetails}
          onSavePacking={onSavePacking}
          onDone={() => setActiveTab('itinerary')}
          bookableIdeas={computeBookableIdeas(allIdeas, selectedIds, placedIdeaIds, trip?.destination)}
        />
      )}

      {activeTab === 'settings' && (
        <div style={{ padding: `24px ${px}px 60px`, maxWidth: 480 }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 22, letterSpacing: -0.3, marginBottom: 24, color: PALETTE.ink }}>Settings</div>
          <div style={{ padding: '20px 0', borderTop: `0.5px solid ${PALETTE.rule}` }}>
            <div style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: PALETTE.ink, marginBottom: 4 }}>Danger zone</div>
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, marginBottom: 16, lineHeight: 1.5 }}>
              Deleting this trip is permanent and cannot be undone.
            </div>
            {showDeleteConfirm ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft }}>Are you sure?</span>
                <button onClick={() => onDelete?.()} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: PALETTE.red, color: '#fff', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 8 }}>Yes, delete trip</button>
                <button onClick={() => setShowDeleteConfirm(false)} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setShowDeleteConfirm(true)} style={{ appearance: 'none', border: `1px solid ${PALETTE.red}`, background: 'transparent', fontFamily: 'Inter', fontSize: 13, color: PALETTE.red, cursor: 'pointer', padding: '8px 16px', borderRadius: 8 }}>Delete trip</button>
            )}
          </div>
        </div>
      )}

      {bookingSheet && (
        <BookMiniSheet
          idea={bookingSheet.idea}
          bookingInfo={bookingSheet.bookingInfo}
          isBooked={!!bookings?.[bookingSheet.idea.id]}
          onToggleBooked={onToggleBooked}
          onClose={() => setBookingSheet(null)}
        />
      )}

      {bookedDetailIdea && (
        <BookedDetailSheet
          idea={bookedDetailIdea}
          details={bookings?.[`${bookedDetailIdea.id}_details`] || {}}
          onClose={() => setBookedDetailIdea(null)}
          onToggleBooked={onToggleBooked}
          onOpenBookings={() => { setBookedDetailIdea(null); setActiveTab('bookings'); }}
        />
      )}

      {legDetail && (
        <LegDetailSheet
          leg={legDetail.leg}
          kind={legDetail.kind}
          onSave={(updated) => saveLegDetail(legDetail.kind, updated)}
          onRemove={() => removeLegDetail(legDetail.kind, legDetail.leg.id)}
          onClose={() => setLegDetail(null)}
        />
      )}

      {showScan && (
        <ScanSheet
          accent={accent}
          onClose={() => setShowScan(false)}
          onApply={(result) => {
            onSaveTripDetails?.(result);
            setShowScan(false);
          }}
        />
      )}

      {showMap && (
        <MapView
          displayDays={displayDays}
          displayOverflow={displayOverflow}
          allIdeas={allIdeas}
          destination={trip?.destination || ''}
          timeOverrides={timeOverrides}
          accent={accent}
          onEdit={(idea) => setEditingItem(idea)}
          onMoveToDay={(ideaId, toDayIdx) => moveToDay(ideaId, toDayIdx)}
          onMoveToParking={(ideaId) => moveToParking(ideaId)}
          onRemove={(ideaId) => removeIdea(ideaId)}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
}
